import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";

const AUTH_IMG = "https://images.unsplash.com/photo-1614064548237-096f735f344f";

export default function LoginPage() {
  const { login } = useAuth();
  const { t, translateError } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/wiki";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(translateError(formatApiError(err.response?.data?.detail)) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative z-10 grid min-h-[calc(100vh-73px)] grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={AUTH_IMG} alt="" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-[#0A0A0A]" />
        <div className="absolute bottom-12 left-12 max-w-sm">
          <h2 className="font-heading text-3xl font-black tracking-tighter">{t("login.sideTitle")}</h2>
          <p className="mt-3 text-muted-foreground">{t("login.sideText")}</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm animate-rise">
          <span className="font-mono text-xs uppercase tracking-widest text-primary">{t("login.eyebrow")}</span>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter">{t("login.title")}</h1>

          {error && (
            <div data-testid="login-error" className="mt-6 border border-lie/40 bg-lie/10 px-4 py-3 text-sm text-lie">{error}</div>
          )}

          <div className="mt-8 space-y-5">
            <div>
              <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("login.email")}</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="border-white/15 bg-card" />
            </div>
            <div>
              <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("login.password")}</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="border-white/15 bg-card" />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="login-submit" className="mt-8 w-full rounded-md bg-primary py-6 font-bold uppercase tracking-wider hover:bg-[#FF645A]">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("login.submit")}
          </Button>

          <p className="mt-6 text-sm text-muted-foreground">
            {t("login.noAccount")}{" "}
            <Link to="/register" className="text-primary hover:underline">{t("login.join")}</Link>
          </p>
        </form>
      </div>
    </main>
  );
}
